import {
  Button,
  Center,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Select,
  Spinner,
  Stack,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Currency,
  EvaluationModelDto,
  NegotiationRoundStatus,
  SourcingEventV2Dto,
  useAwardSourcingEventMutation,
  useGetEvaluationQuery,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { Explanation } from "../../../../common/support/Explanation/Explanation";
import { useFinalStageRound, useRoundSelection } from "../rounds/RoundSelectionContext";
import { AwardBidsTable } from "./AwardBidsTable";
import { AwardDecisionHistory } from "./AwardDecisionHistory";
import { pickDefaultWinner } from "./pickDefaultWinner";
import { SupplierCountIndicator } from "./SupplierCountIndicator";

export const SelectWinner = ({ event }: { event: SourcingEventV2Dto }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const apiError = useApiError();
  const finalStageRound = useFinalStageRound();
  const { rounds } = useRoundSelection();
  // ADR-033 §5: awarding is blocked while any negotiation round is still open.
  const hasOpenRound = rounds.some((round) => round.status !== NegotiationRoundStatus.Completed);
  const { data: evaluation, isLoading, isError } = useGetEvaluationQuery({
    eventId: event.id,
    round: finalStageRound?.id,
  });
  const [award, { isLoading: isAwarding }] = useAwardSourcingEventMutation();

  const [winnerIds, setWinnerIds] = useState<string[]>([]);
  const [internalNote, setInternalNote] = useState("");
  const selectWinnerPublicCtx = event.eventContext.type === "Public" ? event.eventContext : null;
  const [currency, setCurrency] = useState<Currency>(selectWinnerPublicCtx?.estimatedValue?.currency ?? Currency.Nok);
  const [amount, setAmount] = useState(
    selectWinnerPublicCtx?.estimatedValue?.value != null ? String(selectWinnerPublicCtx.estimatedValue.value) : "",
  );

  // ADR-033 D6: shopped products pre-select their suppliers' bids (multi-winner).
  const stageBasket = (finalStageRound ? finalStageRound.selectedProductBids : event.selectedProductBids) ?? [];
  const basketBidIds = [
    ...new Set(stageBasket.flatMap((group) => group.bids.flatMap((product) => product.bids))),
  ].join(",");

  useEffect(() => {
    if (!evaluation) return;
    const validIds = evaluation.bids.map((b) => b.bidId);
    setWinnerIds((prev) => {
      const stillValid = prev.filter((id) => validIds.includes(id));
      if (stillValid.length > 0) return stillValid.length === prev.length ? prev : stillValid;
      // Price evaluation (private): the bids selected during evaluation and the shopped
      // basket decide the default winners; without either the award choice stays with the
      // buyer — nothing is pre-selected.
      if (evaluation.model === EvaluationModelDto.Price) {
        const defaults = [...(evaluation.selectedBidIds ?? []), ...basketBidIds.split(",")];
        return [...new Set(defaults)].filter((id) => id && validIds.includes(id));
      }
      const fallback = pickDefaultWinner(evaluation.bids);
      return fallback ? [fallback] : [];
    });
  }, [evaluation, basketBidIds]);

  const maxParticipants = selectWinnerPublicCtx?.frameworkMaxParticipants ?? undefined;

  const toggleWinner = (bidId: string) =>
    setWinnerIds((prev) => (prev.includes(bidId) ? prev.filter((id) => id !== bidId) : [...prev, bidId]));

  if (isLoading) {
    return (
      <Center py="16">
        <Spinner color="smPrimary" />
      </Center>
    );
  }
  if (isError || !evaluation) {
    return (
      <Center py="16">
        <Text color="smError">{t("Could not load the evaluation.")}</Text>
      </Center>
    );
  }
  if (evaluation.bids.length === 0) {
    return (
      <Center py="16">
        <Text color="smMuted">{t("No delivered bids to award yet.")}</Text>
      </Center>
    );
  }

  const submit = async () => {
    if (winnerIds.length === 0) return;
    const parsed = amount.trim() === "" ? null : Number(amount);
    try {
      await award({
        eventId: event.id,
        awardSourcingEventRequest: {
          winningBidIds: winnerIds,
          internalNote: internalNote.trim() || null,
          eventValue: parsed != null && !Number.isNaN(parsed) ? { value: parsed, currency } : null,
        },
      }).unwrap();
      toast({ title: t("Event awarded"), status: "success", position: "top-right" });
    } catch (e) {
      apiError.trigger(e);
    }
  };

  return (
    <Stack
      spacing="6"
      bg="smBackground"
      rounded="xl"
      border="1px solid"
      borderColor="smBorder"
      p={{ base: "4", md: "6" }}
    >
      <AwardDecisionHistory event={event} />

      <Stack spacing="2">
        <AwardBidsTable evaluation={evaluation} mode="select" selectedBidIds={winnerIds} onToggle={toggleWinner} />
        <SupplierCountIndicator selectedCount={winnerIds.length} maxParticipants={maxParticipants} />
      </Stack>

      <FormControl>
        <FormLabel>{t("Estimated contract value")}</FormLabel>
        <HStack>
          <Input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            bg="smBackground"
            maxW="xs"
            placeholder="0"
          />
          <Select value={currency} onChange={(e) => setCurrency(e.target.value as Currency)} bg="smBackground" maxW="32">
            {Object.values(Currency).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </HStack>
      </FormControl>

      <FormControl>
        <FormLabel>{t("Internal note")}</FormLabel>
        <Textarea value={internalNote} onChange={(e) => setInternalNote(e.target.value)} bg="smBackground" rows={2} />
        <FormHelperText>{t("Only visible to your organization.")}</FormHelperText>
      </FormControl>

      <Flex justify="end" align="center" gap="3">
        <Text fontSize="sm" color="smMuted">
          {t("You'll prepare and send award letters to each supplier after awarding.")}
        </Text>
        <Explanation text={t("Complete all negotiation rounds before awarding")} enabled={hasOpenRound}>
          <Button
            colorScheme="teal"
            onClick={submit}
            isLoading={isAwarding}
            isDisabled={winnerIds.length === 0 || hasOpenRound}
          >
            {t("Award to selected suppliers")}
          </Button>
        </Explanation>
      </Flex>
    </Stack>
  );
};
